import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Paper, Box, Typography, Stack, Tabs, Tab, CircularProgress, Button, Tooltip } from '@mui/material';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { selectedRegionState, getForecastByRegion, getWarehousesByRegion } from '../../../atoms/Forecast/index.js';
import { getRegions } from '../../../atoms/regions/index.js';
import { saveForecast } from '../../../repositories/HttpRepository.js';
import { getBSType } from '../utils/getBSType.js';
import { createForecastDataMap, getZoneInfoForTicketType, getForecastIdByWeekDay, cloneMap, parseMapKey } from '../utils/transformData.js';
import CustomForecastTable from './CustomForecastTable.jsx';
import StickyActionBar from './StickyActionBar.jsx';
import History from '../History/index.jsx';
import { getTicketTypeLabel } from '../../../constants/forecast.js';
import { ToastMessage } from '../../../constants/toastMessage.js';
import { toast } from 'react-toastify';
import Settings from '../Settings/index.jsx';

// Key used when no ticket types exist
const ALL_DATA_KEY = '__ALL__';

const ForecastTable = () => {
  const selectedRegion = useRecoilValue(selectedRegionState);
  const forecastsLoadable = useRecoilValueLoadable(getForecastByRegion);
  const warehousesLoadable = useRecoilValueLoadable(getWarehousesByRegion);
  const warehouses = warehousesLoadable.state === 'hasValue' ? warehousesLoadable.contents : [];
  const regions = useRecoilValue(getRegions);
  const timezone = regions.find((r) => r.value === selectedRegion)?.timezone ?? null;
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editingDataMap, setEditingDataMap] = useState(new Map()); // current editing state
  const [changesMap, setChangesMap] = useState(new Map()); // original data for reset/comparison
  const initialDataMapRef = useRef(new Map()); // only changed items for Save button
  const [forecastByIdMap, setForecastByIdMap] = useState(new Map()); // API forecast + fields patched after save, per forecastId

  // Track previous region to detect region changes
  const prevRegionRef = useRef(selectedRegion);

  // Get forecasts based on loadable state
  const forecasts = forecastsLoadable.state === 'hasValue' ? forecastsLoadable.contents : [];
  const ticketTypes = getBSType(forecasts);

  // Reset all data when region changes
  useEffect(() => {
    if (prevRegionRef.current !== selectedRegion) {
      // Region changed - reset everything
      setEditingDataMap(new Map());
      setChangesMap(new Map());
      setForecastByIdMap(new Map());
      setSelectedTicketType(null);
      initialDataMapRef.current = new Map();
      prevRegionRef.current = selectedRegion;
    }
  }, [selectedRegion]);

  // Initialize data maps when forecasts load
  useEffect(() => {
    if (forecasts.length > 0) {
      const dataMap = createForecastDataMap(forecasts);

      initialDataMapRef.current = cloneMap(dataMap);

      setEditingDataMap(dataMap);

      setChangesMap(new Map());

      setForecastByIdMap(new Map(forecasts.map((f) => [f.id, f])));
    }
  }, [forecasts]);

  // Update selected ticket type when forecasts change
  useEffect(() => {
    if (ticketTypes.length > 0 && !selectedTicketType) {
      setSelectedTicketType(ticketTypes[0]);
    } else if (ticketTypes.length === 0 && forecasts.length > 0) {
      // No ticket types but has data: use ALL_DATA_KEY
      setSelectedTicketType(ALL_DATA_KEY);
    }
  }, [ticketTypes, selectedTicketType, forecasts.length]);

  const handleTabChange = (event, newValue) => {
    setSelectedTicketType(newValue);
  };

  // Get zone info and forecastId mapping for current ticket type
  const currentTicketTypeFilter = selectedTicketType === ALL_DATA_KEY ? null : selectedTicketType;

  const zoneInfoList = useMemo(() => {
    return getZoneInfoForTicketType(forecasts, currentTicketTypeFilter);
  }, [forecasts, currentTicketTypeFilter]);

  const forecastIdByWeekDay = useMemo(() => {
    return getForecastIdByWeekDay(forecasts, currentTicketTypeFilter);
  }, [forecasts, currentTicketTypeFilter]);

  // Merges the saved payload into the forecasts after API success - patches: [{ id, ...updatedFields }]
  const onForecastsSaved = (patches) => {
    setForecastByIdMap((prev) => {
      const next = new Map(prev);
      patches.forEach(({ id, ...fields }) => next.set(id, { ...next.get(id), ...fields }));
      return next;
    });
  };

  const onCellChange = useCallback((mapKey, updates) => {
    if (!mapKey) return;

    setEditingDataMap((prev) => {
      const newMap = new Map(prev);
      const currentItem = newMap.get(mapKey);
      if (!currentItem) return prev;

      const updatedItem = { ...currentItem, ...updates };
      newMap.set(mapKey, updatedItem);
      return newMap;
    });

    // Update changesMap based on comparison with initial data
    setChangesMap((prev) => {
      const newChangesMap = new Map(prev);
      const initialItem = initialDataMapRef.current.get(mapKey);

      if (!initialItem) {
        newChangesMap.delete(mapKey);
        return newChangesMap;
      }

      const updatedItem = { ...updates };

      if (updatedItem.average_ticket !== initialItem.average_ticket) {
        newChangesMap.set(mapKey, updatedItem);
      } else {
        newChangesMap.delete(mapKey);
      }

      return newChangesMap;
    });
  }, []);

  // Update zone-level data (warehouse/name) - called after API success
  // This updates ALL items for this zone across all weekdays
  const onUpdateZone = useCallback((dataChanges) => {
    if (!dataChanges || dataChanges.length === 0) return;

    setEditingDataMap((prev) => {
      const newMap = new Map(prev);
      dataChanges.forEach((dataChange) => {
        const { mapKey, formData } = dataChange;
        newMap.set(mapKey, { ...newMap.get(mapKey), ...formData });
      });
      return newMap;
    });

    // Also update initial data since it's already saved to API
    dataChanges.forEach((dataChange) => {
      const { mapKey, formData } = dataChange;
      initialDataMapRef.current.set(mapKey, { ...initialDataMapRef.current.get(mapKey), ...formData });
    });
  }, []);

  // Update day-level info data - called after API success
  const onUpdateInfo = useCallback((mapKey, updates) => {
    if (!mapKey) return;

    setEditingDataMap((prev) => {
      const newMap = new Map(prev);
      const currentItem = newMap.get(mapKey);
      if (currentItem) {
        newMap.set(mapKey, { ...currentItem, ...updates });
      }
      return newMap;
    });

    // Also update initial data since it's already saved to API
    const initialItem = initialDataMapRef.current.get(mapKey);
    if (initialItem) {
      initialDataMapRef.current.set(mapKey, { ...initialItem, ...updates });
    }
  }, []);

  const handleReset = useCallback(() => {
    setEditingDataMap(cloneMap(initialDataMapRef.current));
    setChangesMap(new Map());
  }, []);

  const handleSave = useCallback(async () => {
    if (changesMap.size === 0) {
      return;
    }

    const changedItems = Array.from(changesMap.entries()).map(([mapKey, item]) => {
      const { forecastId, zoneId } = parseMapKey(mapKey);
      return {
        id: forecastId,
        groups: [
          {
            id: zoneId,
            average_ticket: item.average_ticket,
          },
        ],
      };
    });

    setSaving(true);
    try {
      const response = await saveForecast(changedItems);

      if (response.ok) {
        // Saved cells now hold user numbers - BE's updated_by only arrives on the next fetch
        const savedDataMap = cloneMap(editingDataMap);
        changesMap.forEach((_, mapKey) => savedDataMap.set(mapKey, { ...savedDataMap.get(mapKey), isJustSavedByCurrentUser: true }));
        initialDataMapRef.current = cloneMap(savedDataMap);
        setEditingDataMap(savedDataMap);
        setChangesMap(new Map());
        toast.success(ToastMessage.UPDATED_SUCCESS);
      } else {
        // console.error('Failed to save forecast:', response);
        toast.error(ToastMessage.UPDATED_FAIL);
      }
    } catch (error) {
      // console.error('Error saving forecast:', error);
      toast.error(ToastMessage.UPDATED_FAIL);
    } finally {
      setSaving(false);
    }
  }, [changesMap, editingDataMap]);

  // Check if there are any unsaved changes
  const hasChanges = changesMap.size > 0;

  const renderEmptyState = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body1">Select a region to view forecast numbers</Typography>
      </Box>
    );
  };

  const renderLoadingState = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  };

  const renderBody = () => {
    return (
      <>
        <Typography variant="caption">
          Review the{' '}
          <Typography as="span" variant="caption">
            pre-populated forecast numbers
          </Typography>{' '}
          (last week&apos;s actual routes × the{' '}
          <Typography as="span" variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
            forecast % set for each day
          </Typography>
          ) and edit if needed.
        </Typography>

        {/* Tabs for ticket types */}
        {ticketTypes.length > 0 && (
          <Tabs value={selectedTicketType} onChange={handleTabChange} sx={{ minHeight: 40 }}>
            {ticketTypes.map((ticketType) => (
              <Tab
                key={ticketType}
                label={getTicketTypeLabel(ticketType)}
                value={ticketType}
                sx={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  minHeight: 40,
                }}
              />
            ))}
          </Tabs>
        )}

        {/* Render forecast table */}
        {zoneInfoList.length > 0 && (
          <>
            <CustomForecastTable
              editingDataMap={editingDataMap}
              initialDataMap={initialDataMapRef.current}
              changesMap={changesMap}
              zoneInfoList={zoneInfoList}
              forecastIdByWeekDay={forecastIdByWeekDay}
              warehouses={warehouses}
              onCellChange={onCellChange}
              onUpdateZone={onUpdateZone}
              onUpdateInfo={onUpdateInfo}
              forecastByIdMap={forecastByIdMap}
              timezone={timezone}
              onAutoModeSaved={onForecastsSaved}
            />
            <StickyActionBar>
              <Tooltip title="Discard unsaved changes">
                <Button variant="outlined" color="primary" onClick={handleReset} disabled={saving || !hasChanges}>
                  Cancel
                </Button>
              </Tooltip>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !hasChanges}>
                {saving ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </StickyActionBar>
          </>
        )}

        {zoneInfoList.length === 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body1">No forecast data</Typography>
          </Box>
        )}
      </>
    );
  };

  const renderContent = () => {
    if (!selectedRegion) {
      return renderEmptyState();
    }

    if (forecastsLoadable.state === 'loading') {
      return renderLoadingState();
    }

    if (forecastsLoadable.state === 'hasError') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body1" color="error">
            Error loading forecasts
          </Typography>
        </Box>
      );
    }

    return renderBody();
  };

  return (
    <Paper sx={{ height: '100%', flex: 1, padding: 2 }}>
      <Stack direction="column" sx={{ gap: 0 }}>
        <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Forecast Table
          </Typography>
          <Box>
            <History />
            <Settings itemProps={{ 'auto-forecast': { forecasts, forecastByIdMap, initialTicketType: currentTicketTypeFilter, onSaved: onForecastsSaved } }} />
          </Box>
        </Stack>
        {renderContent()}
      </Stack>
    </Paper>
  );
};

export default ForecastTable;
